import store from '@/store/store'
import { setOpenModalTryPremium, type PremiumPromptContinuation } from '@/store/slices/user/actions'
import { customAmplitude } from '@/utils/customAmplitude'
import {
  buildPremiumPromptProperties,
  buildSignupAnalyticsContext,
  type SignupPremiumType,
} from '@/utils/signupPremiumAnalytics'

export interface OpenPremiumPromptOptions {
  /**
   * `BtSubscription<Feature>` source. Drives both the modal's copy and which
   * plan it opens on — see `SUPER_INITIAL_SOURCES` in `metsikonPlanConfig`.
   */
  source: string
  /** 'Standard' for the Premium paywall, 'Super' for the Super upsell. */
  type: SignupPremiumType
  /** Re-run after a successful upgrade. */
  callback?: () => void
  continuation?: PremiumPromptContinuation
}

/**
 * Opens the Try Premium / Upgrade to Super modal with the full analytics
 * payload — Amplitude *and* the GTM `dataLayer` push, plus `opened: true` so the
 * onboarding flow doesn't re-prompt on top of it.
 *
 * Extracted from `TrySuperButton`, which was the only place doing all three
 * correctly; the AI-tools gates were dispatching a bare action and losing the
 * dataLayer event and the attribution context.
 */
export function openPremiumPrompt({ source, type, callback, continuation }: OpenPremiumPromptOptions): void {
  const track = () => {
    const eventProperties = buildPremiumPromptProperties(
      buildSignupAnalyticsContext({
        source,
        type,
        pathname: typeof window !== 'undefined' ? window.location.pathname : undefined,
      }),
    )
    customAmplitude('Premium Prompt', eventProperties)
    if (typeof window !== 'undefined') {
      // @ts-ignore — dataLayer is injected by GTM.
      window.dataLayer?.push({ event: 'premium_prompt', ...eventProperties })
    }
  }
  let tracked = false
  const trackedContinuation = continuation && {
    ...continuation,
    onVisible: () => {
      if (tracked) return true
      if (!continuation.onVisible()) return false
      tracked = true
      track()
      return true
    },
  }
  store.dispatch(setOpenModalTryPremium({ isOpen: true, source, opened: true, callback,
    ...(trackedContinuation ? { continuation: trackedContinuation } : {}),
  }))
  if (!continuation) track()
}
